import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { claimsAPI } from '../services/api';
import 'leaflet/dist/leaflet.css';
import './ClaimLocationMap.css';

type Position = { lat: number; lng: number };

interface ClaimLocationMapProps {
  postcode: string;
  onCoordinatesChange: (position: Position) => void;
  onError: (message: string | null) => void;
}

const markerIcon = L.divIcon({
  className: 'claim-map-marker',
  html: '<span></span>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const MapClickHandler: React.FC<{ onSelect: (position: Position) => void }> = ({ onSelect }) => {
  useMapEvents({
    click(event) {
      onSelect({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
};

const RecenterMap: React.FC<{ position: Position }> = ({ position }) => {
  const map = useMap();
  useEffect(() => { map.setView([position.lat, position.lng], 16); }, [map, position.lat, position.lng]);
  return null;
};

const ClaimLocationMap: React.FC<ClaimLocationMapProps> = ({ postcode, onCoordinatesChange, onError }) => {
  const [position, setPosition] = useState<Position | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const trimmedPostcode = postcode.replace(/\s+/g, '').toUpperCase();
    const completeUkPostcode = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(trimmedPostcode);
    if (!trimmedPostcode || !completeUkPostcode) {
      setPosition(null);
      onError(null);
      return;
    }

    const lookup = async () => {
      setLoading(true);
      try {
        const response = await claimsAPI.getPostcodeLocation(trimmedPostcode);
        const nextPosition = { lat: response.data.latitude as number, lng: response.data.longitude as number };
        setPosition(nextPosition);
        onCoordinatesChange(nextPosition);
        onError(null);
      } catch (requestError: any) {
        setPosition(null);
        onError(requestError.response?.data?.message || 'The postcode could not be located. Check it and try again.');
      } finally {
        setLoading(false);
      }
    };

    const timeout = window.setTimeout(lookup, 500);
    return () => window.clearTimeout(timeout);
  }, [postcode, onCoordinatesChange, onError]);

  if (!postcode.trim()) return <p className="claim-map-hint">Enter an incident postcode to load the location map.</p>;
  if (loading) return <p className="claim-map-hint">Locating postcode...</p>;
  if (!position) return null;

  const selectPosition = (nextPosition: Position) => {
    setPosition(nextPosition);
    onCoordinatesChange(nextPosition);
  };

  return (
    <section className="claim-location-map" aria-label="Incident location map">
      <div className="claim-location-map-heading">
        <strong>Pinpoint collision location</strong>
        <span>Drag the red pin, or click the map, to update the What3Words address.</span>
      </div>
      <MapContainer center={[position.lat, position.lng]} zoom={16} scrollWheelZoom className="claim-map">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxNativeZoom={19}
          maxZoom={19}
        />
        <RecenterMap position={position} />
        <MapClickHandler onSelect={selectPosition} />
        <Marker
          position={[position.lat, position.lng]}
          icon={markerIcon}
          draggable
          eventHandlers={{
            dragend(event) {
              const marker = event.target as L.Marker;
              const nextPosition = marker.getLatLng();
              selectPosition({ lat: nextPosition.lat, lng: nextPosition.lng });
            },
          }}
        />
      </MapContainer>
    </section>
  );
};

export default ClaimLocationMap;
